import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getJson } from '@/lib/http';
import { downloadAuthenticatedFile, AuthenticatedDownloadError } from '@/lib/downloadAuthenticated';

type SubmissionRow = {
  taskId: number;
  postingEventId: number;
  operatorName: string;
  storeId?: number | null;
  storeName: string;
  campaignName: string;
  taskType?: string;
  reviewStatus: string;
  rejectionButton: string;
  rejectionReasons: string;
  rejectionText: string;
  isOutstanding?: boolean;
};

type ReviewCounts = {
  submitted: number;
  approved: number;
  rejected: number;
  outstanding: number;
  pending: number;
  rejectRate: number | null;
};

type OperatorReviewCounts = ReviewCounts & { operatorName: string };
type CampaignReviewCounts = ReviewCounts & { campaignName: string };

export type SubmissionReviewReportResponse = {
  rows: SubmissionRow[];
  summary: ReviewCounts & {
    byOperator?: OperatorReviewCounts[];
    byCampaign?: CampaignReviewCounts[];
  };
};

export type ReviewReportView = 'rejected' | 'campaigns' | 'installers';
export type ReviewTaskKind = 'both' | 'posting' | 'removal';

type StatusFilter = 'outstanding' | 'rejected' | 'all' | 'approved' | 'pending';

function matchesTaskKind(taskType: string | undefined, kind: ReviewTaskKind): boolean {
  const normalized = (taskType ?? '').trim().toUpperCase();
  switch (kind) {
    case 'both':
      return true;
    case 'removal':
      return normalized === 'REMOVAL';
    case 'posting':
      return normalized !== 'REMOVAL';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function taskTypeLabel(taskType: string | undefined): string {
  const normalized = (taskType ?? '').trim().toUpperCase();
  if (normalized === 'REMOVAL') return 'Removal';
  if (normalized === 'REPOST') return 'Repost';
  return 'Posting';
}

function buildParams(
  retailerId: number,
  cycleId: number,
  operatorNameContains: string,
  campaignId?: number | null,
  taskKind: ReviewTaskKind = 'both',
  storeId?: number | null,
): URLSearchParams {
  const params = new URLSearchParams({
    retailerId: String(retailerId),
    cycleId: String(cycleId),
  });
  if (operatorNameContains.trim()) {
    params.set('operatorNameContains', operatorNameContains.trim());
  }
  if (campaignId != null && Number.isFinite(campaignId)) {
    params.set('campaignId', String(campaignId));
  }
  if (taskKind !== 'both') {
    params.set('taskKind', taskKind);
  }
  if (storeId != null && Number.isFinite(storeId)) {
    params.set('storeId', String(storeId));
  }
  return params;
}

function statusCellClass(status: string): string {
  if (status === 'Approved') return 'bg-emerald-50 text-emerald-800';
  if (status === 'Rejected') return 'bg-red-50 text-red-800';
  if (status === 'Pending review') return 'bg-amber-50 text-amber-800';
  return '';
}

function formatRejectRate(rate: number | null): string {
  if (rate == null) return '—';
  return `${Math.round(rate * 100)}%`;
}

function rejectRateClass(rate: number | null): string {
  if (rate == null) return '';
  if (rate >= 0.1) return 'bg-red-50 text-red-800';
  if (rate > 0) return 'bg-amber-50 text-amber-800';
  return 'bg-emerald-50 text-emerald-800';
}

interface Props {
  retailerId: number;
  cycleId: number;
  campaignId?: number | null;
  storeId?: number | null;
  view: ReviewReportView;
  taskKind?: ReviewTaskKind;
}

function rateOrNull(rejected: number, reviewed: number): number | null {
  return reviewed > 0 ? rejected / reviewed : null;
}

function summarizeRows(rows: SubmissionRow[]): {
  submitted: number;
  approved: number;
  rejected: number;
  outstanding: number;
  pending: number;
  byCampaign: CampaignReviewCounts[];
  byOperator: OperatorReviewCounts[];
} {
  const byCampaign = new Map<string, ReviewCounts>();
  const byOperator = new Map<string, ReviewCounts>();
  let approved = 0;
  let rejected = 0;
  let outstanding = 0;
  let pending = 0;

  const emptyCounts = (): ReviewCounts => ({
    submitted: 0,
    approved: 0,
    rejected: 0,
    outstanding: 0,
    pending: 0,
    rejectRate: null,
  });

  const bump = (bucket: ReviewCounts, row: SubmissionRow) => {
    bucket.submitted += 1;
    if (row.reviewStatus === 'Approved') bucket.approved += 1;
    else if (row.reviewStatus === 'Rejected') bucket.rejected += 1;
    else bucket.pending += 1;
    if (row.isOutstanding) bucket.outstanding += 1;
  };

  for (const row of rows) {
    if (row.reviewStatus === 'Approved') approved += 1;
    else if (row.reviewStatus === 'Rejected') rejected += 1;
    else pending += 1;
    if (row.isOutstanding) outstanding += 1;

    const campaign = byCampaign.get(row.campaignName) ?? emptyCounts();
    bump(campaign, row);
    byCampaign.set(row.campaignName, campaign);

    const operator = byOperator.get(row.operatorName) ?? emptyCounts();
    bump(operator, row);
    byOperator.set(row.operatorName, operator);
  }

  const withRate = (counts: ReviewCounts): ReviewCounts => ({
    ...counts,
    rejectRate: rateOrNull(counts.rejected, counts.approved + counts.rejected),
  });

  return {
    submitted: rows.length,
    approved,
    rejected,
    outstanding,
    pending,
    byCampaign: [...byCampaign.entries()]
      .map(([campaignName, counts]) => ({ campaignName, ...withRate(counts) }))
      .sort((a, b) => b.rejected - a.rejected || a.campaignName.localeCompare(b.campaignName)),
    byOperator: [...byOperator.entries()]
      .map(([operatorName, counts]) => ({ operatorName, ...withRate(counts) }))
      .sort((a, b) => b.rejected - a.rejected || a.operatorName.localeCompare(b.operatorName)),
  };
}

export function SubmissionReviewReportPanel({
  retailerId,
  cycleId,
  campaignId,
  storeId,
  view,
  taskKind = 'both',
}: Props) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('outstanding');
  const [isExporting, setIsExporting] = useState(false);

  const fetchParams = buildParams(retailerId, cycleId, '', campaignId).toString();
  const exportParams = buildParams(retailerId, cycleId, '', campaignId, taskKind, storeId).toString();

  const { data, isLoading, error } = useQuery<SubmissionReviewReportResponse>({
    queryKey: ['/api/review-reports', retailerId, cycleId, campaignId ?? 'all'],
    queryFn: () => getJson<SubmissionReviewReportResponse>(`/api/review-reports?${fetchParams}`),
    enabled: Number.isFinite(retailerId) && Number.isFinite(cycleId),
  });

  const scopedRows = useMemo(
    () =>
      (data?.rows ?? []).filter((row) => {
        if (!matchesTaskKind(row.taskType, taskKind)) return false;
        if (storeId == null) return true;
        return row.storeId === storeId;
      }),
    [data?.rows, taskKind, storeId],
  );

  const scopedSummary = useMemo(() => summarizeRows(scopedRows), [scopedRows]);

  const visibleRows = useMemo(() => {
    switch (statusFilter) {
      case 'all':
        return scopedRows;
      case 'rejected':
        return scopedRows.filter((row) => row.reviewStatus === 'Rejected');
      case 'approved':
        return scopedRows.filter((row) => row.reviewStatus === 'Approved');
      case 'pending':
        return scopedRows.filter((row) => row.reviewStatus === 'Pending review');
      case 'outstanding':
        return scopedRows.filter((row) => row.isOutstanding);
      default: {
        const _exhaustive: never = statusFilter;
        return _exhaustive;
      }
    }
  }, [scopedRows, statusFilter]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const prefix =
        statusFilter === 'outstanding'
          ? `review-outstanding-cycle-${cycleId}`
          : `review-decisions-cycle-${cycleId}`;
      await downloadAuthenticatedFile(
        `/api/review-reports/export?${exportParams}`,
        `${prefix}-${stamp}.xlsx`,
      );
    } catch (exportError) {
      const msg =
        exportError instanceof AuthenticatedDownloadError
          ? exportError.message
          : exportError instanceof Error
            ? exportError.message
            : String(exportError);
      window.alert(`Failed to export review report:\n${msg}`);
    } finally {
      setIsExporting(false);
    }
  };

  const subtitle = data
    ? `${scopedSummary.outstanding} outstanding · ${scopedSummary.rejected} rejected · ${scopedSummary.approved} approved · ${scopedSummary.pending} pending`
    : 'Submitted tasks with the rejection action, reason, and written note.';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="text-sm text-muted-foreground">{subtitle}</div>
          {view === 'rejected' && statusFilter === 'outstanding' ? (
            <div className="text-xs text-muted-foreground">
              Snagging list — rejects that still need a retake or revisit. Earlier rejects that
              were later submitted again are hidden.
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {view === 'rejected' ? (
            <select
              className="h-8 rounded-md border bg-white px-2 text-sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            >
              <option value="outstanding">Outstanding</option>
              <option value="rejected">All rejected</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="all">All submitted</option>
            </select>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => void handleExport()} disabled={isExporting || isLoading}>
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading reviews...</div>
      ) : error ? (
        <div className="text-sm text-red-600">
          {error instanceof Error ? error.message : 'Failed to load reviews'}
        </div>
      ) : (
        <ReviewViewTable
          view={view}
          rows={visibleRows}
          byCampaign={scopedSummary.byCampaign}
          byOperator={scopedSummary.byOperator}
          showTaskType={taskKind === 'both'}
          emptyMessage={
            statusFilter === 'outstanding'
              ? 'No outstanding rejects in this view.'
              : 'No submissions in this view.'
          }
        />
      )}
    </div>
  );
}

function ReviewViewTable({
  view,
  rows,
  byCampaign,
  byOperator,
  showTaskType,
  emptyMessage,
}: {
  view: ReviewReportView;
  rows: SubmissionRow[];
  byCampaign: CampaignReviewCounts[];
  byOperator: OperatorReviewCounts[];
  showTaskType: boolean;
  emptyMessage: string;
}) {
  switch (view) {
    case 'rejected':
      return <RejectedTasksTable rows={rows} showTaskType={showTaskType} emptyMessage={emptyMessage} />;
    case 'campaigns':
      return <CampaignRollupTable rows={byCampaign} />;
    case 'installers':
      return <InstallerRollupTable rows={byOperator} />;
    default: {
      const _exhaustive: never = view;
      return _exhaustive;
    }
  }
}

function RejectedTasksTable({
  rows,
  showTaskType,
  emptyMessage,
}: {
  rows: SubmissionRow[];
  showTaskType: boolean;
  emptyMessage: string;
}) {
  const columnCount = showTaskType ? 7 : 6;
  return (
    <div className="overflow-auto border rounded-md">
      <table className="w-full min-w-max border-collapse text-sm">
        <thead>
          <tr className="bg-slate-100">
            <th className="border px-3 py-2 text-left font-medium">Store</th>
            {showTaskType ? <th className="border px-3 py-2 text-left font-medium">Type</th> : null}
            <th className="border px-3 py-2 text-left font-medium">Campaign</th>
            <th className="border px-3 py-2 text-left font-medium">Status</th>
            <th className="border px-3 py-2 text-left font-medium">Rejection action</th>
            <th className="border px-3 py-2 text-left font-medium">Reason</th>
            <th className="border px-3 py-2 text-left font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="border px-3 py-6 text-muted-foreground">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={`${row.taskId}-${row.postingEventId}`}>
                <td className="border px-3 py-2 align-top">
                  <div>{row.storeName}</div>
                  <div className="text-xs text-muted-foreground">{row.operatorName}</div>
                </td>
                {showTaskType ? (
                  <td className="border px-3 py-2 align-top">{taskTypeLabel(row.taskType)}</td>
                ) : null}
                <td className="border px-3 py-2 align-top">{row.campaignName}</td>
                <td className={`border px-3 py-2 align-top ${statusCellClass(row.reviewStatus)}`}>
                  {row.reviewStatus}
                </td>
                <td className="border px-3 py-2 align-top">{row.rejectionButton || ''}</td>
                <td className="border px-3 py-2 align-top">{row.rejectionReasons || ''}</td>
                <td className="border px-3 py-2 align-top whitespace-pre-wrap">{row.rejectionText || ''}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function CampaignRollupTable({ rows }: { rows: CampaignReviewCounts[] }) {
  return (
    <div className="overflow-auto border rounded-md">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-100">
            <th className="border px-3 py-2 text-left font-medium">Campaign</th>
            <th className="border px-3 py-2 text-right font-medium">Submitted</th>
            <th className="border px-3 py-2 text-right font-medium">Approved</th>
            <th className="border px-3 py-2 text-right font-medium">Rejected</th>
            <th className="border px-3 py-2 text-right font-medium">Outstanding</th>
            <th className="border px-3 py-2 text-right font-medium">Pending</th>
            <th className="border px-3 py-2 text-right font-medium">Reject rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="border px-3 py-6 text-muted-foreground">
                No campaign totals in this cycle.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.campaignName}>
                <td className="border px-3 py-2">{row.campaignName}</td>
                <td className="border px-3 py-2 text-right">{row.submitted}</td>
                <td className="border px-3 py-2 text-right">{row.approved}</td>
                <td className="border px-3 py-2 text-right">{row.rejected}</td>
                <td className={`border px-3 py-2 text-right ${row.outstanding > 0 ? 'bg-red-50 text-red-800' : ''}`}>
                  {row.outstanding}
                </td>
                <td className="border px-3 py-2 text-right">{row.pending}</td>
                <td className={`border px-3 py-2 text-right ${rejectRateClass(row.rejectRate)}`}>
                  {formatRejectRate(row.rejectRate)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function InstallerRollupTable({ rows }: { rows: OperatorReviewCounts[] }) {
  return (
    <div className="overflow-auto border rounded-md">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-100">
            <th className="border px-3 py-2 text-left font-medium">Installer</th>
            <th className="border px-3 py-2 text-right font-medium">Submitted</th>
            <th className="border px-3 py-2 text-right font-medium">Approved</th>
            <th className="border px-3 py-2 text-right font-medium">Rejected</th>
            <th className="border px-3 py-2 text-right font-medium">Outstanding</th>
            <th className="border px-3 py-2 text-right font-medium">Pending</th>
            <th className="border px-3 py-2 text-right font-medium">Reject rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="border px-3 py-6 text-muted-foreground">
                No installer totals in this cycle.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.operatorName}>
                <td className="border px-3 py-2">{row.operatorName}</td>
                <td className="border px-3 py-2 text-right">{row.submitted}</td>
                <td className="border px-3 py-2 text-right">{row.approved}</td>
                <td className="border px-3 py-2 text-right">{row.rejected}</td>
                <td className={`border px-3 py-2 text-right ${row.outstanding > 0 ? 'bg-red-50 text-red-800' : ''}`}>
                  {row.outstanding}
                </td>
                <td className="border px-3 py-2 text-right">{row.pending}</td>
                <td className={`border px-3 py-2 text-right ${rejectRateClass(row.rejectRate)}`}>
                  {formatRejectRate(row.rejectRate)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
